import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, SelectedOptionI } from '../AutoComplete';
import { useTranslation } from 'react-i18next';
import { throttle, useIntersectionObserver } from '@siberiacancode/reactuse';
import { useGetPortCatalogMutation } from 'app/api/marine-infrastructure';
import { PortCatalogData } from 'ais-facade-api/api';
import { useDebounce } from 'app/hooks/useDebounce';

import { InfoWrapper } from 'VesselCard/InfoWrapper';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { setPortsForDistanceCalc } from 'app/utils/searchSlice';

type PortFieldT = 'from' | 'to';

interface DistancePortSelectI {
  fieldName: PortFieldT;
}
export const DistancePortSelect = ({ fieldName }: DistancePortSelectI) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [getCatalog, { data: portsData, ...catalogRequest }] = useGetPortCatalogMutation({
    fixedCacheKey: 'get-ports-catalog-mutation',
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);

  const getPorts = (term: string, offset: number) => {
    if (term.length < 2) {
      debouncedGetCatalogHandler({
        filter: {},
        order: {
          direction: 'ASC',
          by: 'CITY_NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }

    if (term.length >= 2) {
      debouncedGetCatalogHandler({
        filter: {
          term: term,
        },
        order: {
          direction: 'ASC',
          by: 'CITY_NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }
  };

  const [offset, setOffset] = useState<number>(0);
  const [ports, setPorts] = useState<{ ports: PortCatalogData[] }>({ ports: [] });
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const debouncedValue = useDebounce<string>(inputValue, 500);
  const [options, setOptions] = useState<SelectedOptionI[] | null>(null);

  const storeDistanceData = useAppSelector((state) => state.searchSlice.distanceBetween);

  const initialPlaceholderValue = useMemo(() => storeDistanceData?.[fieldName].name, [storeDistanceData, fieldName]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (inputRef.current?.value) {
      getPorts(inputRef.current?.value, 0);
    }

    if (initialPlaceholderValue && catalogRequest.isUninitialized) {
      setOffset(0);
      setPorts({ ports: [] });
      getPorts(initialPlaceholderValue, 0);
    }
    if (catalogRequest.isUninitialized && !initialPlaceholderValue && !debouncedValue) {
      setPorts({ ports: [] });

      getPorts('', 0);
    }
  }, [initialPlaceholderValue]);

  const handleClose = () => {
    setOpen(false);
  };

  const { ref: scrollableDivRef } = useIntersectionObserver<HTMLDivElement>({
    threshold: 1,
    onChange: (entry) => {
      if (entry.isIntersecting) setOffset((prev) => prev + 10);
    },
  });

  const handleSelect = (selectedOption?: SelectedOptionI | null) => {
    if (selectedOption?.value) {
      const port = ports.ports.filter((port: PortCatalogData) => {
        return port.key?.toLowerCase() === selectedOption?.value?.toLowerCase();
      })?.[0];
      if (port) {
        dispatch(setPortsForDistanceCalc({ ...storeDistanceData, [fieldName]: port }));
      }
    }
  };
  useEffect(() => {
    catalogRequest.reset();
  }, []);

  useEffect(() => {
    if (portsData && portsData.results && portsData.results?.length > 0) {
      setPorts((prev) => {
        const prevPorts = prev.ports;
        const newPorts = portsData.results?.filter((port) => {
          return !prevPorts.some((prevPort) => prevPort.key === port.key);
        });
        return {
          ports: [...prevPorts, ...(newPorts || [])],
        };
      });
    }
  }, [portsData]);

  useEffect(() => {
    const options = ports?.ports.map((port) => ({
      value: port.key,
      placeholder: port.name,
      label: <InfoWrapper value={`${port.un} ${port.locode}`} label={port.name} />,
    }));
    setOptions(options);
  }, [ports]);

  useEffect(() => {
    if (inputRef?.current) {
      setInputValue(inputRef.current.value);
    }
  }, [inputRef?.current]);

  useEffect(() => {
    setOffset(0);
    catalogRequest.reset();

    setPorts({ ports: [] });
    if (!catalogRequest.isUninitialized && open) {
      getPorts(debouncedValue, 0);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!catalogRequest.isUninitialized) {
      getPorts(debouncedValue, offset);
    }
  }, [offset]);
  return (
    <AutoComplete
      placeholder={t('SelectPort')}
      onChange={setInputValue}
      id={`autocomplete-port_${fieldName}`}
      initialValue={initialPlaceholderValue}
      staticSearch={false}
      inputRef={inputRef}
      divRef={scrollableDivRef}
      loading={catalogRequest.isLoading}
      options={options}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onSelect={handleSelect}
      isSuccess={catalogRequest.isSuccess}
      hasmore={portsData?.results?.length !== 0}
    />
  );
};
